import { useMemo, useState } from 'react'

import { SecondaryActionItem } from '@factorialco/f0-react/dist/experimental'
import { ArrowCycle, Upload } from '@factorialco/f0-react/icons/app'

import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { Modals, ModalsProps } from 'modules/integrations/components/SyncButton/Modals'
import { SyncFiltersModal } from 'modules/integrations/components/SyncFiltersModal'
import { usePrivateConditionalIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import type { SyncFilterDefinition } from 'modules/integrations/lib/syncFilters'

/**
 * Base shape for sync actions. Tooltip follows SecondaryActionItem's function
 * signature — consumers needing a plain string (e.g. Header) must unwrap it.
 */
export type SyncActionBase = Pick<
  SecondaryActionItem,
  'label' | 'icon' | 'loading' | 'onClick' | 'disabled' | 'tooltip'
> & {
  onClick: () => void
}

export type UseSyncActionProps = {
  onConfirmSync?: ModalsProps['onConfirm']
  confirmTrackingEvent?: string
  disabled?: boolean
  hidden?: boolean
  tooltip?: string
  syncFilters?: SyncFilterDefinition[]
  skipConfirmationIfNoIssues?: boolean
}

type UseSyncActionReturn = {
  syncActions: SyncActionBase[]
  syncModal: React.ReactNode | null
}

type ModalStep = 'closed' | 'filters' | 'issues'

export const usePrivateSyncAction = ({
  onConfirmSync,
  confirmTrackingEvent,
  disabled = false,
  hidden = false,
  tooltip,
  syncFilters = [],
  skipConfirmationIfNoIssues = false,
}: UseSyncActionProps = {}): UseSyncActionReturn => {
  const context = usePrivateConditionalIntegrationsSyncContext(hidden)
  const capabilityMode = context?.capabilityMode
  const syncEnabled = context?.syncEnabled ?? false
  const marketplaceIntegration = context?.marketplaceIntegration
  const isSyncing = context?.isSyncing ?? false
  const totalItems = context?.totalItems
  const isListEmpty = totalItems === 0

  const hasFilters = syncFilters.length > 0

  const [modalStep, setModalStep] = useState<ModalStep>('closed')

  const isExportMode = capabilityMode === 'file'
  const canShowSyncAction = !hidden && syncEnabled && marketplaceIntegration

  const handleClose = () => {
    setModalStep('closed')
  }

  const syncActions = useMemo<SyncActionBase[]>(() => {
    if (canShowSyncAction) {
      const noItemsTooltip = isExportMode
        ? i18n.t('integrations.sync_button.no_items_export_tooltip')
        : i18n.t('integrations.sync_button.no_items_sync_tooltip')

      return [
        {
          tooltip: ({ disabled: isDisabled }: { disabled: boolean; loading: boolean }) => {
            if (!isDisabled) return tooltip
            if (tooltip) return tooltip
            return isListEmpty ? noItemsTooltip : undefined
          },
          label: isExportMode
            ? i18n.t('compensations.integrations.export_action', {
                integration_name: marketplaceIntegration.displayName,
              })
            : i18n.t('compensations.integrations.sync_action', {
                integration_name: marketplaceIntegration.displayName,
              }),
          icon: isExportMode ? Upload : ArrowCycle,
          loading: isSyncing,
          onClick: () => {
            setModalStep(hasFilters ? 'filters' : 'issues')
            if (confirmTrackingEvent) track(confirmTrackingEvent)
          },
          disabled: disabled || isListEmpty,
        },
      ]
    }
    return []
  }, [
    canShowSyncAction,
    marketplaceIntegration,
    isExportMode,
    isSyncing,
    confirmTrackingEvent,
    disabled,
    isListEmpty,
    tooltip,
    hasFilters,
  ])

  const renderModal = () => {
    if (modalStep === 'filters') {
      return (
        <SyncFiltersModal
          syncFilters={syncFilters}
          skipConfirmationIfNoIssues={skipConfirmationIfNoIssues}
          onClose={handleClose}
          onContinue={() => setModalStep('issues')}
        />
      )
    }

    if (modalStep === 'issues') {
      return (
        <Modals
          setShowModal={(show) => {
            if (!show) handleClose()
          }}
          onConfirm={onConfirmSync}
          skipConfirmationIfNoIssues={skipConfirmationIfNoIssues}
        />
      )
    }

    return null
  }

  return {
    syncActions,
    syncModal: renderModal(),
  }
}
